import React from 'react';
import {
  Table,
  Checkbox,
  Text,
  Badge,
  Group,
  Stack,
  ScrollArea,
  Button,
  Tooltip,
  Flex
} from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { ImportQuestion } from './ImportQuestionModal';

/* eslint-disable */
interface QuestionPreviewTableProps {
  questions: ImportQuestion[];
  onQuestionsChange: (questions: ImportQuestion[]) => void;
}
/* eslint-enable */

const QuestionPreviewTable: React.FC<QuestionPreviewTableProps> = ({
  questions,
  onQuestionsChange
}) => {
  const handleQuestionToggle = (index: number, selected: boolean) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index].selected = selected;
    onQuestionsChange(updatedQuestions);
  };

  const handleSelectAll = () => {
    const updatedQuestions = questions.map((q) => ({ ...q, selected: true }));
    onQuestionsChange(updatedQuestions);
  };

  const handleDeselectAll = () => {
    const updatedQuestions = questions.map((q) => ({ ...q, selected: false }));
    onQuestionsChange(updatedQuestions);
  };

  const selectedCount = questions.filter((q) => q.selected).length;

  // Helper function to parse categories
  const parseCategories = (categoryString?: string) => {
    if (!categoryString) return [];
    return categoryString
      .split(',')
      .map((cat) => cat.trim())
      .filter((cat) => cat.length > 0);
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Range':
        return 'blue';
      case 'Choose One':
        return 'green';
      case 'Free-text':
        return 'orange';
      default:
        return 'gray';
    }
  };

  if (questions.length === 0) {
    return (
      <div
        style={{
          padding: '40px 20px',
          backgroundColor: '#F8F9FA',
          borderRadius: '12px',
          border: '1px solid #E9ECEF',
          textAlign: 'center'
        }}
      >
        <Stack align='center' gap='md'>
          <div
            style={{
              padding: '16px',
              backgroundColor: '#868E96',
              borderRadius: '50%',
              display: 'inline-flex'
            }}
          >
            <IconX size={32} color='white' />
          </div>
          <div>
            <Text size='md' fw={600} mb='xs' c='#2C2E33'>
              No questions found in the uploaded file
            </Text>
            <Text size='sm' c='#868E96'>
              Please check your file format and try again
            </Text>
          </div>
        </Stack>
      </div>
    );
  }

  return (
    <Stack gap='md'>
      <div
        style={{
          padding: '20px',
          backgroundColor: '#F8F9FA',
          borderRadius: '12px',
          border: '1px solid #E9ECEF'
        }}
      >
        <Group justify='space-between' align='center'>
          <div>
            <Text fw={600} size='md' c='#2C2E33' mb='xs'>
              {selectedCount} of {questions.length} questions selected
            </Text>
            <Text size='sm' c='#868E96'>
              Review and select the questions you want to import
            </Text>
          </div>

          <Group gap='sm'>
            <Button
              size='sm'
              variant='outline'
              onClick={handleSelectAll}
              leftSection={<IconCheck size={14} />}
              styles={{
                root: {
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  height: '32px',
                  borderColor: '#7B2183',
                  color: '#7B2183',
                  '&:hover': {
                    backgroundColor: '#F3F0FF'
                  }
                }
              }}
            >
              Select All
            </Button>
            <Button
              size='sm'
              variant='outline'
              onClick={handleDeselectAll}
              leftSection={<IconX size={14} />}
              styles={{
                root: {
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  height: '32px',
                  borderColor: '#DDD',
                  color: '#495057',
                  '&:hover': {
                    backgroundColor: '#F8F9FA'
                  }
                }
              }}
            >
              Deselect All
            </Button>
          </Group>
        </Group>
      </div>

      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #E9ECEF',
          overflow: 'hidden'
        }}
      >
        <ScrollArea h={400}>
          <Table
            striped={false}
            highlightOnHover
            styles={{
              thead: {
                backgroundColor: '#F8F9FA'
              },
              th: {
                fontWeight: 600,
                fontSize: '13px',
                color: '#495057',
                padding: '16px 12px',
                borderBottom: '1px solid #E9ECEF'
              },
              td: {
                padding: '16px 12px',
                verticalAlign: 'top',
                fontSize: '14px',
                borderBottom: '1px solid #F1F3F4'
              },
              tr: {
                '&:hover': {
                  backgroundColor: '#F8F9FA'
                }
              }
            }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={60}>
                  <Group gap='xs' align='center'>
                    <IconCheck size={14} color='#7B2183' />
                    <Text size='xs' fw={600} c='#495057'>
                      SELECT
                    </Text>
                  </Group>
                </Table.Th>
                <Table.Th w={50}>
                  <Text size='xs' fw={600} c='#495057'>
                    ROW
                  </Text>
                </Table.Th>
                <Table.Th>
                  <Text size='xs' fw={600} c='#495057'>
                    QUESTION DETAILS
                  </Text>
                </Table.Th>
                <Table.Th w={100}>
                  <Text size='xs' fw={600} c='#495057'>
                    TYPE
                  </Text>
                </Table.Th>
                <Table.Th w={160}>
                  <Text size='xs' fw={600} c='#495057'>
                    CATEGORIES
                  </Text>
                </Table.Th>
                <Table.Th w={180}>
                  <Text size='xs' fw={600} c='#495057'>
                    RANGE OPTIONS
                  </Text>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {questions.map((question, index) => {
                const categories = parseCategories(question.category);

                return (
                  <Table.Tr
                    key={index}
                    style={{
                      backgroundColor: question.selected ? '#F3F0FF' : 'white',
                      borderLeft: question.selected ? '3px solid #7B2183' : '3px solid transparent'
                    }}
                  >
                    <Table.Td>
                      <Checkbox
                        checked={question.selected}
                        onChange={(event) =>
                          handleQuestionToggle(index, event.currentTarget.checked)
                        }
                        size='md'
                        color='#7B2183'
                        styles={{
                          input: {
                            borderRadius: '4px'
                          }
                        }}
                      />
                    </Table.Td>

                    <Table.Td>
                      <Badge
                        variant='outline'
                        size='sm'
                        color='gray'
                        styles={{
                          root: {
                            fontSize: '12px',
                            fontWeight: 600
                          }
                        }}
                      >
                        {question.rowIndex}
                      </Badge>
                    </Table.Td>

                    <Table.Td>
                      <Stack gap='xs'>
                        <Text size='sm' fw={600} lineClamp={2} c='#2C2E33'>
                          {question.title || (
                            <Text c='#FA5252' fs='italic' fw={500}>
                              No title provided
                            </Text>
                          )}
                        </Text>

                        {question.description && (
                          <Text size='xs' c='#868E96' lineClamp={2}>
                            {question.description}
                          </Text>
                        )}

                        {question.skalaCategory && (
                          <Badge
                            size='xs'
                            variant='dot'
                            color='violet'
                            styles={{
                              root: {
                                fontSize: '11px',
                                fontWeight: 500
                              }
                            }}
                          >
                            {question.skalaCategory}
                          </Badge>
                        )}
                      </Stack>
                    </Table.Td>

                    <Table.Td>
                      <Badge
                        size='sm'
                        variant='light'
                        color={getTypeColor(question.type)}
                        styles={{
                          root: {
                            fontWeight: 600,
                            fontSize: '12px'
                          }
                        }}
                      >
                        {question.type}
                      </Badge>
                    </Table.Td>

                    <Table.Td>
                      {categories.length > 0 ? (
                        <Stack gap='xs'>
                          <Flex wrap='wrap' gap='xs'>
                            {categories.slice(0, 2).map((cat, catIndex) => (
                              <Badge
                                key={catIndex}
                                size='xs'
                                variant='outline'
                                color='#7B2183'
                                styles={{
                                  root: {
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '11px'
                                  }
                                }}
                              >
                                {cat}
                              </Badge>
                            ))}
                            {categories.length > 2 && (
                              <Tooltip
                                label={
                                  <Stack gap='xs'>
                                    {categories.slice(2).map((cat, idx) => (
                                      <Text key={idx} size='xs'>
                                        {cat}
                                      </Text>
                                    ))}
                                  </Stack>
                                }
                                styles={{
                                  tooltip: {
                                    backgroundColor: '#2C2E33',
                                    fontSize: '12px'
                                  }
                                }}
                              >
                                <Badge
                                  size='xs'
                                  variant='outline'
                                  color='gray'
                                  style={{ cursor: 'pointer' }}
                                  styles={{
                                    root: {
                                      fontSize: '11px',
                                      fontWeight: 500
                                    }
                                  }}
                                >
                                  +{categories.length - 2}
                                </Badge>
                              </Tooltip>
                            )}
                          </Flex>
                        </Stack>
                      ) : (
                        <Text size='xs' c='#ADB5BD' fs='italic'>
                          No categories
                        </Text>
                      )}
                    </Table.Td>

                    <Table.Td>
                      {question.type === 'Range' &&
                      question.rangeItems &&
                      question.rangeItems.length > 0 ? (
                        <Stack gap='xs'>
                          {question.rangeItems.slice(0, 2).map((item, itemIndex) => (
                            <Group key={itemIndex} gap='xs' wrap='nowrap'>
                              <Badge
                                size='xs'
                                variant='filled'
                                color='blue'
                                styles={{
                                  root: {
                                    minWidth: '24px',
                                    fontSize: '11px',
                                    fontWeight: 600
                                  }
                                }}
                              >
                                {item.value}
                              </Badge>
                              <Text size='xs' lineClamp={1} c='#495057'>
                                {item.label}
                              </Text>
                            </Group>
                          ))}
                          {question.rangeItems.length > 2 && (
                            <Tooltip
                              label={
                                <Stack gap='xs'>
                                  {question.rangeItems.slice(2).map((item, idx) => (
                                    <Group key={idx} gap='xs'>
                                      <Badge size='xs' variant='filled' color='blue'>
                                        {item.value}
                                      </Badge>
                                      <Text size='xs'>{item.label}</Text>
                                    </Group>
                                  ))}
                                </Stack>
                              }
                              styles={{
                                tooltip: {
                                  backgroundColor: '#2C2E33',
                                  fontSize: '12px'
                                }
                              }}
                            >
                              <Text size='xs' c='#339AF0' style={{ cursor: 'pointer' }} fw={500}>
                                +{question.rangeItems.length - 2} more
                              </Text>
                            </Tooltip>
                          )}
                        </Stack>
                      ) : question.type === 'Range' ? (
                        <Text size='xs' c='#FA5252' fs='italic' fw={500}>
                          No range items
                        </Text>
                      ) : (
                        <Text size='xs' c='#ADB5BD'>
                          Not applicable
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </div>
    </Stack>
  );
};

export default QuestionPreviewTable;
